import * as Y from 'yjs';

import { type BaseErrorOptions } from '@canvas/error';
import { RecoverableNetworkError, UnrecoverableNetworkError } from '@canvas/retry';
import { type TokenManager } from '@canvas/token-manager';

import { type CollabStrategy, getCollabStrategy } from '../collab-strategy';

import { fetchWithTimeout } from './fetch-with-timeout';

export class BoardDocumentMissingError extends UnrecoverableNetworkError {
  constructor(options?: BaseErrorOptions) {
    super('document does not exist', 'not-found', {
      name: 'BoardDocumentMissingError',
      ...options,
    });
  }
}

export class NoConnectionError extends RecoverableNetworkError {
  constructor(options?: BaseErrorOptions) {
    super('no connection to canvas servers', { name: 'NoConnectionError', ...options });
  }
}

interface DocumentRequestOptions {
  serverUrl: string;
  documentId: string;
  signal: AbortSignal;
  headers?: Record<string, string>;
  tokenManager: TokenManager;
  versionId?: string;
}

export const fetchInitialDocumentStateV2 = async ({
  serverUrl,
  documentId,
  headers,
  signal,
  tokenManager,
  versionId,
}: DocumentRequestOptions) => {
  const token = await tokenManager.tokenForDocumentIdOrThrow(documentId);
  const opts: RequestInit = {
    headers: {
      'x-access-token': token.accessToken,
      ...headers,
    },
    signal,
  };

  const params = {
    skipSteps: 'true',
    v: '2',
    ...(versionId && versionId !== 'latest' && { versionId }),
  };
  const searchParams = new URLSearchParams(params);
  const canvasServerUrl = `${serverUrl}/api/_internal/collab/${encodeURIComponent(
    documentId,
  )}?${searchParams}`;

  const result = await fetchWithTimeout(canvasServerUrl, opts, {
    timeout: 60000,
  });

  if (!result.ok) {
    if (result.status === 401) {
      throw new RecoverableNetworkError(
        `Expired token attempting to fetch document ${documentId}`,
      );
    } else if (result.status === 403) {
      throw new UnrecoverableNetworkError(
        `Not allowed to perform initial document fetch. Access denied.`,
      );
    } else if (result.status === 404) {
      throw new BoardDocumentMissingError();
    } else if (result.status === 408) {
      throw new RecoverableNetworkError(
        `Timed out attempting to fetch document ${documentId}`,
      );
    } else if (result.status >= 500) {
      throw new RecoverableNetworkError(
        `Unable to establish connection for document ${documentId} but can retry`,
      );
    } else {
      throw new RecoverableNetworkError(
        `Unable to establish connection for document ${documentId}`,
      );
    }
  }

  const documentBuffer = await result.arrayBuffer();
  const headStep = result.headers.get('X-Server-Head-Step');
  const untrustedSnapshot = result.headers.get('X-Server-Snapshot-Untrusted') === 'true';
  // canvas-server sets the header based on the existing collab infra set for this document
  const collabInfrastructureHeader = result.headers.get('X-Collab-Infrastructure');
  const useNcs = collabInfrastructureHeader === 'ncs';
  const collabStrategy = getCollabStrategy(useNcs ? 'ncs' : 'cf');

  if (!documentBuffer) {
    throw new RecoverableNetworkError(`Response did not specify a relay to join`);
  }

  return {
    document: new Uint8Array(documentBuffer),
    headStep,
    untrustedSnapshot,
    collabStrategy,
  };
};

export type GetDocumentV2Opts = {
  documentId: string;
  serverUrl: string | undefined;
  signal: AbortSignal;
  tokenManager: TokenManager;
  headers?: Record<string, string>;
  versionId?: string;
  handleUntrustedSnapshot?: (untrusted: boolean) => void;
};

export type RemoteDocument = {
  document: Y.Doc;
  headStep: string | null;
  collabStrategy: CollabStrategy;
};

export async function getDocumentV2({
  documentId,
  serverUrl,
  signal,
  tokenManager,
  headers,
  versionId,
  handleUntrustedSnapshot,
}: GetDocumentV2Opts): Promise<RemoteDocument> {
  const document = new Y.Doc();
  if (serverUrl) {
    const result = await fetchInitialDocumentStateV2({
      serverUrl: serverUrl,
      documentId,
      headers,
      signal,
      tokenManager,
      versionId,
    });

    if (handleUntrustedSnapshot) {
      handleUntrustedSnapshot(result.untrustedSnapshot);
    }

    Y.applyUpdate(document, result.document);
    return {
      document,
      headStep: result.headStep,
      collabStrategy: result.collabStrategy,
    };
  }

  const collabStrategy = getCollabStrategy('cf');
  return { document, headStep: null, collabStrategy };
}
